/*
 * impulse-card-carousel.js
 *
 * Per-card image carousel for product cards — a port of Impulse's
 * assets/ss-grid-carousel.js onto Dawn's card DOM.
 *
 * Behaviour copied from the original:
 *   - product images are fetched from /products/<handle>.js
 *   - capped at the first 6 images; cards with fewer than 2 are skipped
 *   - prev/next arrows and a dot per image are injected over the image
 *   - swipe on touch devices, with the card link suppressed after a swipe
 *   - cards are initialised lazily through an IntersectionObserver
 *
 * Adapted for Dawn:
 *   - card root is .card-wrapper[data-product-handle] (Impulse: .grid-product)
 *   - the image frame is .card__inner (Impulse: .grid-product__image-mask)
 *   - Dawn's hover "secondary image" is hidden while the carousel is active,
 *     exactly as Impulse hid .grid-product__secondary-image
 */
(function () {
  'use strict';

  if (window.__IMPULSE_CARD_CAROUSEL_LOADED) return;
  window.__IMPULSE_CARD_CAROUSEL_LOADED = true;

  var MAX_IMAGES = 6; // Impulse: images.length > 6 -> slice(0, 6)
  var cache = {};

  function fetchProduct(handle) {
    if (!cache[handle]) {
      cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
      })
        .then(function (r) { return r.ok ? r.json() : { images: [] }; })
        .catch(function () { return { images: [] }; });
    }
    return cache[handle];
  }

  function withWidth(url, w) {
    if (!url) return url;
    return url.split('?')[0] + '?width=' + w;
  }

  function preload(images) {
    images.forEach(function (src) {
      var pre = new Image();
      pre.decoding = 'async';
      pre.src = withWidth(src, 720);
    });
  }

  function arrow(dir) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ss-carousel-arrow ss-carousel-arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
    b.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
      'stroke-linecap="round" stroke-linejoin="round"><polyline points="' +
      (dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6') +
      '"/></svg>';
    return b;
  }

  function build(card, frame, img, images) {
    if (frame.querySelector('.ss-carousel-arrow, .ss-carousel-dots')) return;

    frame.classList.add('ss-carousel-active');
    preload(images);

    var prev = arrow('prev');
    var next = arrow('next');
    var dots = document.createElement('div');
    dots.className = 'ss-carousel-dots';

    var current = 0;
    var suppressLinkClick = false;

    function go(i) {
      i = ((i % images.length) + images.length) % images.length;
      current = i;
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      img.sizes = '';
      img.loading = 'eager';
      img.src = withWidth(images[i], 720);
      Array.prototype.forEach.call(dots.children, function (d, idx) {
        d.classList.toggle('is-active', idx === i);
      });
    }

    images.forEach(function (_, i) {
      var d = document.createElement('button');
      d.type = 'button';
      d.className = 'ss-carousel-dot' + (i === 0 ? ' is-active' : '');
      d.setAttribute('aria-label', 'Image ' + (i + 1));
      d.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        go(i);
      });
      dots.appendChild(d);
    });

    function stop(e) { e.preventDefault(); e.stopPropagation(); }
    prev.addEventListener('click', function (e) { stop(e); go(current - 1); });
    next.addEventListener('click', function (e) { stop(e); go(current + 1); });

    frame.appendChild(prev);
    frame.appendChild(next);
    frame.appendChild(dots);

    // the whole card is a link in Dawn — stop the arrows/dots navigating away
    frame.addEventListener(
      'click',
      function (e) {
        if (e.target.closest('.ss-carousel-arrow, .ss-carousel-dots, .ss-carousel-dot')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (suppressLinkClick) { e.preventDefault(); e.stopPropagation(); }
      },
      true
    );

    // swipe (touch only), same thresholds as the Impulse original
    var startX = null, startY = null, pointerId = null;
    function endSwipe(x, y) {
      if (startX == null) return;
      var dx = x - startX, dy = y - startY;
      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        suppressLinkClick = true;
        window.setTimeout(function () { suppressLinkClick = false; }, 450);
        go(dx < 0 ? current + 1 : current - 1);
      }
      startX = startY = pointerId = null;
    }
    if (window.PointerEvent) {
      frame.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse') return;
        if (e.target.closest('.ss-carousel-arrow, .ss-carousel-dots, .ss-carousel-dot')) return;
        pointerId = e.pointerId; startX = e.clientX; startY = e.clientY;
      }, true);
      frame.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'mouse' || pointerId !== e.pointerId || startX == null) return;
        var dx = e.clientX - startX, dy = e.clientY - startY;
        if (Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy) && e.cancelable) e.preventDefault();
      }, { passive: false, capture: true });
      frame.addEventListener('pointerup', function (e) {
        if (e.pointerType !== 'mouse' && pointerId === e.pointerId) endSwipe(e.clientX, e.clientY);
      }, true);
      frame.addEventListener('pointercancel', function () { startX = startY = pointerId = null; }, true);
    }

    img.setAttribute('draggable', 'false');
  }

  function init(card) {
    if (card.__ssCarousel) return;
    var handle = card.getAttribute('data-product-handle');
    if (!handle) return;
    var frame = card.querySelector('.card__inner');
    if (!frame) return;
    var img = frame.querySelector('.card__media img');
    if (!img) return;

    card.__ssCarousel = true;
    fetchProduct(handle).then(function (data) {
      var images = data && data.images ? data.images.filter(Boolean) : [];
      if (images.length > MAX_IMAGES) images = images.slice(0, MAX_IMAGES);
      if (images.length < 2) return;
      build(card, frame, img, images);
    });
  }

  function start(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll('.card-wrapper[data-product-handle]');
    if (!cards.length) return;
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { init(en.target); io.unobserve(en.target); }
        });
      }, { rootMargin: '200px 0px' });
      cards.forEach(function (c) { if (!c.__ssCarousel) io.observe(c); });
    } else {
      cards.forEach(init);
    }
  }

  function boot() {
    start();
    if (window.MutationObserver) {
      var target = document.querySelector('.collection') || document.body;
      new MutationObserver(function (muts) {
        var refresh = muts.some(function (m) { return m.addedNodes && m.addedNodes.length; });
        if (refresh) window.requestAnimationFrame(function () { start(target); });
      }).observe(target, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { start(e.target); });
  window.ImpulseCardCarousel = { refresh: start };
})();
